<script setup>
import { mergeDeep } from '@0x234/common-util';
import CallToActionBanner from 'components/CallToActionBanner.vue';
import ErrorSection from 'components/ErrorSection.vue';
import JobDetail from 'components/JobDetail.vue';
import MoreJobsSection from 'components/MoreJobsSection.vue';
import { useSeoManager } from 'composables/seo-manager';
import env from 'configs/env';
import { metaDefaults } from 'configs/meta';
import { useMeta } from 'quasar';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { useJobViewerStore } from 'stores/job-viewer-store';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

defineOptions({
    async preFetch({ store, currentRoute, redirect }) {
        const jobScopeId = currentRoute.params.jobScopeId;
        const jobViewerStore = useJobViewerStore(store);
        try {
            await jobViewerStore.loadJobByJobScopeId(jobScopeId);
            await jobViewerStore.loadSimilarJobs(jobScopeId);
        } catch (err) {
            if (err?.response?.status === 404) {
                return redirect(AppRoute.build(Route.PageNotFoundErrorPage), 404);
            }
            return redirect(AppRoute.build(Route.UnknownErrorPage), 500);
        }
    }
});

const $route = useRoute();
const $router = useRouter();
const jobViewerStore = useJobViewerStore();
const seoManager = useSeoManager();

const moreJobsSectionElement = ref(),
    isMoreJobsSectionVisible = computed(() => {
        return moreJobsSectionElement.value?.isVisible;
    });

const job = computed(() => {
        const jobScopeId = parseInt($route.params.jobScopeId, 10);
        return jobViewerStore.findJobByJobScopeId(jobScopeId);
    }),
    isJobPaused = computed(() => {
        return !job.value.owner && job.value?.status === 'Paused';
    }),
    isJobExpired = computed(() => {
        return !job.value.owner && job.value?.status === 'Expired';
    });

const jobScopeId = computed(() => {
    const jobScopeId = parseInt($route.params.jobScopeId, 10);
    if (isNaN(jobScopeId)) {
        return null;
    }
    return jobScopeId;
});

const canonicalUrl = computed(() => {
    const path = $route.fullPath;
    return `${env.clientBaseURL}${path}`;
});

const jobJsonLd = seoManager.generateJobJsonLd(job, canonicalUrl.value);

useMeta(() => {
    let title = 'Job';
    let description = 'Discover exciting job opportunities and advance your career with Xprt Jobs.';
    let ogDescription = 'Explore a wide range of positions and companies to advance your professional journey, and find your next career opportunity with Xprt Jobs.';
    if (job.value) {
        const { title: jobTitle, company, scopes } = job.value;
        const locationName = scopes[0]?.location?.name;
        title = `${jobTitle} at ${company.name}`;
        description = `Explore the ${jobTitle} position at ${company.name}${locationName ? ` in ${locationName}` : ''}, offering a unique opportunity to advance your career in a dynamic work environment with Xprt Jobs.`;
        ogDescription = `Discover an exciting ${jobTitle} role at ${company.name}, where you can leverage your skills and experience to make a significant impact in your field through Xprt Jobs.`;
    }
    const logoUrl = job.value?.logoUrl || job.value?.company?.logoUrl;
    const pageMeta = {
        title,
        meta: {
            description: {
                name: 'description',
                content: description
            },
            ogTitle: {
                property: 'og:title',
                content: title
            },
            ogDescription: {
                property: 'og:description',
                content: ogDescription
            },
            // Always include robots meta tag with at least "noimageindex"
            robots: {
                name: 'robots',
                content: 'noimageindex,follow'
            }
        },
        link: [
            {
                rel: 'canonical',
                href: canonicalUrl.value
            }
        ],
        // Add JSON+LD script to meta
        script: jobJsonLd.value ? [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify(jobJsonLd.value)
            }
        ] : []
    };

    if (logoUrl) {
        pageMeta.meta.ogImage = {
            property: 'og:image',
            content: logoUrl
        };
    }
    // Override with "noindex" directive for paused or expired jobs
    if (isJobPaused.value || isJobExpired.value) {
        pageMeta.meta.robots.content = 'noindex,noimageindex,follow';
    }
    // Add unavailable_after meta tag if job has an expiration date
    if (job.value?.expiresAt) {
        // Format the ISO timestamp to RFC-850 format for unavailable_after directive
        const expiresDate = new Date(job.value.expiresAt);
        const formattedDate = expiresDate.toUTCString();
        pageMeta.meta.unavailableAfter = {
            name: 'robots',
            content: `unavailable_after: ${formattedDate}`
        };
    }
    return mergeDeep(metaDefaults, pageMeta);
});
</script>

<template>
    <div id="job-page" :class="[{ 'with-more-jobs-section': isMoreJobsSectionVisible }]">
        <div class="container-md q-px-md">
            <template v-if="isJobPaused">
                <ErrorSection>
                    <template #headline>
                        This job is taking a small break.
                    </template>
                    <template #details>
                        It shows signs of returning soon &mdash; but until then, we've got plenty of other great opportunities waiting for you!
                    </template>
                </ErrorSection>
            </template>
            <template v-else-if="isJobExpired">
                <ErrorSection>
                    <template #headline>
                        This job is no longer available.
                    </template>
                    <template #details>
                        Typically, this means the position has been filled or the advertiser has removed the listing early &mdash; but don't worry, we've got plenty more great
                        opportunities to explore that are still waiting for you to apply!
                    </template>
                </ErrorSection>
            </template>
            <template v-else>
                <JobDetail v-if="job" :job/>
            </template>
        </div>
        <CallToActionBanner @button:click="() => $router.push(AppRoute.build(Route.SearchResultsPage))" class="q-mt-xl">
            <template #heading>Looking for More Opportunities?</template>
            <template #subheading>Explore more jobs with upfront salary details that respect your worth.</template>
        </CallToActionBanner>
        <MoreJobsSection ref="moreJobsSectionElement" v-if="jobScopeId" :job-scope-id="jobScopeId"/>
    </div>
</template>

<style scoped lang="scss">
#job-page {
    flex: 1;
    padding-top: 40px;

    > .q-card {
        background: rgba($grey-3, 0.4);
        overflow: auto;

        .body--dark & {
            background: rgba($white, 0.07);
        }

        > .q-card__section {
            max-height: 100%;
            overflow-y: auto;
        }
    }
}
</style>
